import _ from 'lodash';
import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import copilot from '@janus.team/janus-copilot/cjs/janus-copilot';
import particles, { Page } from '@janus.team/janus-particles';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CustomerGoogleAuthStatus } from 'organizations/customer-google-auth-status';
import * as Sentry from '@sentry/browser';
import { googleOauth, RackspaceAccountProvider } from 'customer/context';
import { ProductModelProvider } from 'customer/product-model-context';
import { ProjectsPage } from 'projects/project-list/projects-page';
import { OauthStatusVerification } from 'customer/oauth-status-verification';
import { OnboardingStatusVerification } from 'customer/onboarding-status-verification';
import { NotificationsToaster } from 'notifications/index';
import { TenantVerifier } from './customerView/components/TenantVerifier';
import { Error404 as NotFoundRoute } from './components/error-404/index';
import { RackspaceAccount } from './rackspace-account/index';
import { Search } from './search/index';
import { copilotApplicationContainerProps } from './config';

const environment = process.env.REACT_APP_ENVIRONMENT || 'local';

const RackerAppReady = () => {
  Sentry.setContext('UserExtra', { type: 'Racker' });
  React.useEffect(() => {
    global.document.querySelector('body').classList.remove('customer');
    global.document.querySelector('body').classList.add('racker');
  }, []);
  const { session } = React.useContext(copilot.context.session);
  if (environment !== 'local') {
    const saml = session?.session?.saml;
    const identity = session?.session.identity;
    // https://app.datadoghq.com/rum/explorer?query=service%3Agcp-ui

    window.DD_RUM.onReady(() => {
      window.DD_RUM.setUser({
        id: identity?.access?.user?.id,
        user_id: identity?.access?.user?.id,
        user_name: identity?.access?.user?.name,
        user_email: saml?.email,
        user_type: 'racker',
        user_domain: identity?.access?.user?.['RAX-AUTH:domainId'],
      });
    });
  }

  return (
    <RackspaceAccountProvider>
      <ProductModelProvider>
        <Page>
          <Page.Main>
            <Switch>
              <Route path="/racker/customers/:rackspaceAccountNumber/projects" component={RackspaceAccount} />
              <Redirect from="/racker/customers/:rackspaceAccountNumber" to="/racker/customers/:rackspaceAccountNumber/projects" />
              <Route path="/racker/customers" component={Search} />
              <Redirect to="/racker/customers" />
              <Route component={NotFoundRoute} />
            </Switch>
          </Page.Main>
        </Page>
      </ProductModelProvider>
    </RackspaceAccountProvider>
  );
};

const RackerApp = () => {
  _.set(global, 'isRacker', true);
  return (
    <copilot.ApplicationContainer {...copilotApplicationContainerProps({ janusUi: 'gcp-racker', racker: true })}>
      <copilot.ApplicationContainer.Authenticating>
        <copilot.ApplicationContainer.FanaticGuyLogo>
          Authenticating...
        </copilot.ApplicationContainer.FanaticGuyLogo>
      </copilot.ApplicationContainer.Authenticating>
      <copilot.ApplicationContainer.Ready>
        <RackerAppReady />
      </copilot.ApplicationContainer.Ready>
    </copilot.ApplicationContainer>
  );
};

const CustomerAppReady = () => {
  const [googleOauthStatus, setGoogleOauthStatus] = React.useState(null);
  Sentry.setContext('UserExtra', { type: 'Customer' });

  const { session } = React.useContext(copilot.context.session);
  if (environment !== 'local') {
    const saml = session?.session?.saml;
    const identity = session?.session.identity;
    // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#identify-user-session
    window.DD_RUM.onReady(() => {
      window.DD_RUM.setUser({
        id: identity?.access?.user?.id,
        user_id: identity?.access?.user?.id,
        user_name: identity?.access?.user?.name,
        user_email: saml?.email,
        user_type: 'customer',
        user_domain: identity?.access?.user?.['RAX-AUTH:domainId'],
      });
    });
  }

  return (
    <>
      <RackspaceAccountProvider>
        <ProductModelProvider>
          <googleOauth.Provider value={{ googleOauthStatus, setGoogleOauthStatus }}>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GCP_OAUTH_CLIENT_ID}>
              <TenantVerifier>
                <>
                  <OauthStatusVerification>
                    <CustomerGoogleAuthStatus />
                    <OnboardingStatusVerification>
                      <Switch>
                        <Route path="/projects">
                          <ProjectsPage />
                        </Route>
                        <Route>
                          <Redirect to="/projects" />
                        </Route>
                      </Switch>
                    </OnboardingStatusVerification>
                  </OauthStatusVerification>
                </>
              </TenantVerifier>
            </GoogleOAuthProvider>
          </googleOauth.Provider>
        </ProductModelProvider>
      </RackspaceAccountProvider>
      <NotificationsToaster />
    </>
  );
};

const CustomerApp = () => {
  _.set(global, 'isRacker', false);
  React.useEffect(() => {
    global.document.querySelector('body').classList.add('customer');
    global.document.querySelector('body').classList.remove('racker');
  }, []);
  return (
    <copilot.ApplicationContainer {...copilotApplicationContainerProps({ janusUi: 'gcp-customer', racker: false })}>
      <copilot.ApplicationContainer.Authenticating>
        <copilot.ApplicationContainer.LoadingIndicator>
          Loading...
        </copilot.ApplicationContainer.LoadingIndicator>
      </copilot.ApplicationContainer.Authenticating>
      <copilot.ApplicationContainer.Ready>
        <CustomerAppReady />
      </copilot.ApplicationContainer.Ready>
    </copilot.ApplicationContainer>
  );
};

export const App = () => (
  <particles.configuration.ConfigurationProvider componentMaturity={{ level: { feedback: 'none', unstable: 'none' } }}>
    <Switch>
      <Route path="/racker*" component={RackerApp} />
      <Route path="/*" component={CustomerApp} />
    </Switch>
  </particles.configuration.ConfigurationProvider>
);

export default App;
